import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Check } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { LIMITS } from "../lib/limits";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Reusable direct-to-object-storage uploader. Returns the full public URL via onUploaded.
export function FileUpload({ label, accept = "image/*", onUploaded, testid, hint }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [name, setName] = useState("");

  const isVideo = accept.includes("video");

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Videos still have a hard cap; oversized images are auto-compressed server-side.
    if (isVideo && file.size > LIMITS.VIDEO_MB * 1024 * 1024) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`Video is too large (${sizeMb}MB). Maximum allowed is ${LIMITS.VIDEO_MB}MB.`);
      e.target.value = "";
      return;
    }
    const willCompress = !isVideo && file.size > LIMITS.IMAGE_MB * 1024 * 1024;
    setBusy(true); setDone(false); setName(willCompress ? `Optimizing ${file.name}…` : file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(`${BACKEND}${data.url}`);
      setDone(true);
      setName(file.name);
      toast.success(data.compressed ? "Uploaded — large image optimized to fit." : "Uploaded!");
    } catch (err) { toast.error(`Upload failed: ${apiErr(err)}`); }
    setBusy(false);
  };

  return (
    <div>
      <label className="text-sm font-bold">{label}</label>
      <label className="mt-1 flex items-center gap-2 border border-dashed border-gray-300 px-3 py-2.5 cursor-pointer hover:border-pti-red transition-colors" data-testid={testid}>
        {busy ? <Loader2 size={16} className="animate-spin text-pti-red" /> : done ? <Check size={16} className="text-green-600" /> : <Upload size={16} className="text-gray-500" />}
        <span className="text-sm text-gray-600 truncate">{busy ? "Uploading…" : name || "Choose a file to upload"}</span>
        <input type="file" accept={accept} onChange={handle} className="hidden" />
      </label>
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
